import { Link } from 'react-router';
import { FullScreenSpinner, Logo } from '@/components/ui';
import { useDevEmails, useMe } from '@/lib/queries';
import { NotFound } from './NotFound';

const URL_RE = /(https?:\/\/[^\s)]+)/g;
const isUrl = (s: string) => /^https?:\/\//.test(s);
/** Short button label for a dev link: "Confirm email →" instead of a 300-character URL. */
function linkLabel(url: string) {
  if (url.includes('verify-email')) return 'Confirm email →';
  if (url.includes('reset-password')) return 'Reset password →';
  return 'Open link →';
}

export function DevMailbox() {
  const me = useMe();
  const on = !!me.data?.features.devMailbox;
  const emails = useDevEmails(on);
  if (me.isPending) return <FullScreenSpinner />;
  // Production has no mailbox: the address is just an unknown page there.
  if (!on) return <NotFound />;
  return (
    <div className="screen">
      <div className="topbar">
        <Logo />
        <Link to="/login" style={{ fontSize: 13, fontWeight: 600 }}>
          Log in
        </Link>
      </div>
      <div
        style={{
          maxWidth: 720,
          width: '100%',
          margin: '0 auto',
          padding: '0 24px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em' }}>Local mailbox</h1>
        <p className="muted" style={{ fontSize: 13.5 }}>
          Emails the API would have sent. Configure RESEND_API_KEY to deliver them for real.
        </p>
        {(emails.data ?? []).map((e) => (
          <div
            key={e.id}
            className="card card-pad"
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {/* A long subject wraps; the date keeps a gap, or drops to its own line, right-aligned. */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: '4px 16px',
                fontSize: 12.5,
              }}
            >
              <span
                data-testid="mail-subject"
                style={{ flex: '1 1 260px', minWidth: 0, overflowWrap: 'anywhere' }}
              >
                <b>{e.subject}</b> → {e.to}
              </span>
              <span
                data-testid="mail-date"
                className="faint"
                style={{ flex: 'none', whiteSpace: 'nowrap', marginLeft: 'auto' }}
              >
                {new Date(e.createdAt).toLocaleString()}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
                color: '#333',
                overflowWrap: 'anywhere',
              }}
            >
              {e.text.split(URL_RE).map((part, i) =>
                isUrl(part) ? (
                  <a
                    key={i}
                    href={part.replace(/^https?:\/\/[^/]+/, '')}
                    className="btn btn-primary hv"
                    style={{ display: 'inline-flex', padding: '6px 12px', fontSize: 12.5 }}
                  >
                    {linkLabel(part)}
                  </a>
                ) : (
                  <span key={i}>{part}</span>
                ),
              )}
            </div>
          </div>
        ))}
        {emails.data && emails.data.length === 0 && (
          <div className="muted" style={{ fontSize: 13 }}>
            Nothing yet.
          </div>
        )}
      </div>
    </div>
  );
}
